/**
 * FASE 10 — Feature Flag Gate.
 *
 * Renderizza i children solo se il flag è enabled. Altrimenti mostra un
 * banner di sospensione (o un fallback custom).
 *
 * Uso in App.js:
 *   <Route path="/noleggio" element={
 *     <FeatureFlagGate flag="noleggio_enabled">
 *       <NoleggoPage />
 *     </FeatureFlagGate>
 *   } />
 *
 * Il codice del modulo protetto rimane 100% inalterato — si riattiva
 * cambiando enabled=true in /app/backend/data/feature_flags.json o via
 * admin UI /admin/feature-flags.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// cache in-memory (evita fetch ripetuto)
let _flagsCache = null;
let _flagsCachedAt = 0;
const CACHE_TTL_MS = 60_000;

async function fetchFlags() {
  const now = Date.now();
  if (_flagsCache && (now - _flagsCachedAt) < CACHE_TTL_MS) return _flagsCache;
  try {
    const res = await fetch(`${API}/feature-flags`);
    const data = await res.json();
    _flagsCache = data.flags || {};
    _flagsCachedAt = now;
    return _flagsCache;
  } catch {
    return {};
  }
}

export default function FeatureFlagGate({ flag, children, fallback = null }) {
  const [state, setState] = useState({ loading: true, enabled: false, meta: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const flags = await fetchFlags();
      if (cancelled) return;
      const meta = flags[flag] || null;
      setState({
        loading: false,
        enabled: meta ? Boolean(meta.enabled) : true,  // default = abilitato se flag sconosciuto
        meta,
      });
    })();
    return () => { cancelled = true; };
  }, [flag]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFA] flex items-center justify-center"
            data-testid={`ff-loading-${flag}`}>
        <div className="animate-pulse text-[#4A5E51]">Caricamento…</div>
      </div>
    );
  }

  if (state.enabled) return children;

  if (fallback) return fallback;

  const label = state.meta?.label || "Modulo";
  const msg = state.meta?.suspended_message
    || "Questo modulo è temporaneamente sospeso.";

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-24 pb-16"
            data-testid={`ff-suspended-${flag}`}>
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white border border-[#D9C49A]/60 rounded-2xl p-8 sm:p-10 text-center shadow-sm">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D9C49A]/30 flex items-center justify-center">
              <Lock className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl text-[#0F1F14] mb-3"
                data-testid={`ff-suspended-title-${flag}`}>
              {label} — Sospeso
            </h1>
            <p className="text-[#4A5E51] leading-relaxed mb-6"
                data-testid={`ff-suspended-message-${flag}`}>
              {msg}
            </p>
            <p className="text-xs text-[#7A8E83] mb-6">
              Ci scusiamo per il disagio. Il servizio sarà riattivato appena
              possibile.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#163321] text-white text-sm font-medium hover:bg-[#1d4a2e] transition-colors"
                    data-testid={`ff-suspended-home-${flag}`}>
                <ArrowLeft className="w-4 h-4" /> Torna alla home
              </Link>
              <a href="mailto:info@colucciassicuratori.com"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#163321]/40 text-[#163321] text-sm font-medium hover:bg-[#163321]/5 transition-colors"
                  data-testid={`ff-suspended-contact-${flag}`}>
                <Mail className="w-4 h-4" /> Contatta l'agenzia
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
